import { useEffect, useMemo, useState, type FormEvent, type JSX, type ReactNode } from 'react';
import { Icon } from './Icon.js';
import { DIAL_CODES, type DialEntry } from './dial-codes.js';
import { BRAND } from '../branding.js';

export interface CheckoutFormProduct {
  slug: string;
  name: string;
  description?: string;
  priceRub: number;
  priceIls: number;
  allpayInstallmentsEnabled?: boolean;
  allpayMaxInstallments?: number;
  subscriptionEnabled?: boolean;
  soldOut?: boolean;
}

export type CheckoutMode = 'card' | 'split';

const PHONE_MIN = 4;
const PHONE_MAX = 15;

const FX_FROM_ILS = { EUR: 0.245, USD: 0.265 };
const FX_FROM_RUB = { EUR: 0.0103, USD: 0.0112 };

function fmtAmountWithSym(amountMinor: number, currency: 'RUB' | 'ILS'): string {
  const major = amountMinor / 100;
  const opts = { minimumFractionDigits: 0, maximumFractionDigits: 2 };
  const sym = currency === 'RUB' ? '₽' : '₪';
  return `${sym} ${major.toLocaleString('ru-RU', opts)}`;
}

interface Props {
  product: CheckoutFormProduct;
  mode?: CheckoutMode;
  currency?: 'RUB' | 'ILS';
  onCurrencyChange?: (c: 'RUB' | 'ILS') => void;
  onError?: (msg: string) => void;
  /** Применённый промокод. Отправляется в /api/checkout body. */
  promoCode?: string | null;
  /**
   * Сумма к оплате в minor units после применения промокода. Если undefined —
   * показываем product.price* как обычно. Backend всё равно пересчитает amount
   * сам по promoCode — это поле только для отображения кнопки Submit.
   */
  discountedAmount?: number | null;
  /**
   * Слот для UI промокода. Рендерится сразу под блоком «Сумма к оплате»
   * (только в mode="card"). На десктопе (mode="split") промо — в левой колонке
   * Standalone.tsx, поэтому слот там не используется.
   */
  promoSlot?: ReactNode;
}

export function CheckoutForm({
  product,
  mode = 'card',
  currency: currencyProp,
  onCurrencyChange,
  onError,
  promoCode,
  discountedAmount,
  promoSlot,
}: Props): JSX.Element {
  const [currencyState, setCurrencyState] = useState<'RUB' | 'ILS'>('ILS');
  const currency = currencyProp ?? currencyState;
  const setCurrency = onCurrencyChange ?? setCurrencyState;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dial, setDial] = useState<DialEntry>(DIAL_CODES[0]!);
  const [dialOpen, setDialOpen] = useState(false);
  const [dialTouched, setDialTouched] = useState(false);
  const [dialSearch, setDialSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [messengerType, setMessengerType] = useState<'telegram' | 'whatsapp'>('telegram');
  const [messengerValue, setMessengerValue] = useState('');
  const [installments, setInstallments] = useState(false);
  const [teudatZehut, setTeudatZehut] = useState('');
  const [teudatTouched, setTeudatTouched] = useState(false);
  const installmentsAvailable =
    currency === 'ILS' && product.allpayInstallmentsEnabled === true;
  const installmentsMax = Math.min(
    12,
    Math.max(1, Math.trunc(product.allpayMaxInstallments ?? 12)),
  );
  const installmentsOn = installmentsAvailable && installments;
  const teudatDigits = teudatZehut.replace(/\D/g, '');
  const teudatValid = /^\d{9}$/.test(teudatDigits);

  useEffect(() => {
    if (dialTouched) return;
    fetch('/api/geo')
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { country?: string | null } | null) => {
        if (!data?.country) return;
        const found = DIAL_CODES.find((d) => d.cc === data.country);
        if (found) setDial(found);
      })
      .catch(() => undefined);
  }, [dialTouched]);

  const price = currency === 'RUB' ? product.priceRub : product.priceIls;
  const displayAmount = discountedAmount != null && discountedAmount < price ? discountedAmount : price;
  const phoneDigits = phone.replace(/\D/g, '').length;
  const phoneValid = phoneDigits >= PHONE_MIN && phoneDigits <= PHONE_MAX;
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const nameWords = name.trim().split(/\s+/).filter((w) => w.length > 0);
  const nameValid = nameWords.length >= 2 && nameWords.every((w) => w.length >= 2);
  const soldOut = product.soldOut === true;
  const valid =
    !soldOut && nameValid && validEmail && phoneValid && (!installmentsOn || teudatValid);

  const filteredDial = useMemo(() => {
    const q = dialSearch.trim().toLowerCase();
    if (!q) return DIAL_CODES;
    return DIAL_CODES.filter(
      (d) =>
        d.label.toLowerCase().includes(q) ||
        d.code.replace('+', '').startsWith(q.replace('+', '')) ||
        d.cc.toLowerCase() === q,
    );
  }, [dialSearch]);

  const fxLine = useMemo(() => {
    const major = displayAmount / 100;
    const rates = currency === 'ILS' ? FX_FROM_ILS : FX_FROM_RUB;
    const fmt = (n: number): string =>
      n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return `≈ € ${fmt(major * rates.EUR)} / $ ${fmt(major * rates.USD)}`;
  }, [displayAmount, currency]);

  const onPay = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const trimmedMsg = messengerValue.trim();
      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: product.slug,
          currency,
          customerName: name,
          customerEmail: email,
          customerPhone: dial.code + phone.replace(/\D/g, ''),
          customerMessengerType: trimmedMsg ? messengerType : null,
          customerMessengerValue: trimmedMsg || null,
          installments: installmentsOn,
          customerTeudatZehut: installmentsOn ? teudatDigits : undefined,
          promoCode: promoCode || undefined,
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string; detail?: string } | null;
        throw new Error(data?.detail ?? data?.error ?? `http_${res.status}`);
      }
      const data = (await res.json()) as { redirectUrl: string };
      window.location.href = data.redirectUrl;
    } catch (err) {
      onError?.((err as Error).message);
      setSubmitting(false);
    }
  };

  const isSplit = mode === 'split';

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: isSplit ? 500 : 460 }}>
      <form
        onSubmit={onPay}
        aria-hidden={soldOut || undefined}
        style={{
          width: '100%',
          background: 'var(--surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-md)',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          filter: soldOut ? 'blur(4px)' : undefined,
          pointerEvents: soldOut ? 'none' : undefined,
          userSelect: soldOut ? 'none' : undefined,
          transition: 'filter 180ms ease',
        }}
      >
      {!isSplit && (
        <div>
          <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginBottom: 6 }}>
            Вы оплачиваете
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: -0.4,
              lineHeight: 1.2,
            }}
          >
            <bdi>{product.name}</bdi>
          </div>
        </div>
      )}

      {!isSplit && product.priceRub > 0 && product.priceIls > 0 && (
        <div style={{ marginTop: -4 }}>
          <button
            type="button"
            onClick={() => setCurrency(currency === 'ILS' ? 'RUB' : 'ILS')}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: 'var(--accent)',
              fontSize: 13,
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            {currency === 'ILS' ? 'Оплатить в рублях' : 'Оплатить в любой валюте'}
          </button>
        </div>
      )}

      {!isSplit && product.description && (
        <div
          style={{
            fontSize: 14,
            color: 'var(--fg-muted)',
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            // Каждая строка — в своём направлении: абзац на иврите справа налево.
            unicodeBidi: 'plaintext',
          }}
        >
          {product.description}
        </div>
      )}

      {!isSplit && (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>Сумма к оплате</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              {displayAmount < price && (
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: 'var(--fg-subtle)',
                    textDecoration: 'line-through',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {fmtAmountWithSym(price, currency)}
                </span>
              )}
              <span
                style={{
                  fontSize: 26,
                  fontWeight: 600,
                  letterSpacing: -0.5,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtAmountWithSym(displayAmount, currency)}
              </span>
              {product.subscriptionEnabled && (
                <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--fg-subtle)' }}>
                  в месяц
                </span>
              )}
            </div>
          </div>
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              color: 'var(--fg-subtle)',
              fontVariantNumeric: 'tabular-nums',
              textAlign: 'right',
            }}
          >
            {fxLine}
          </div>
          {promoSlot && <div style={{ marginTop: 10 }}>{promoSlot}</div>}
          <div style={{ marginTop: 12, borderBottom: '1px solid var(--hairline)' }} />
        </div>
      )}

      <div className="field">
        <label className="field-label">Имя и фамилия</label>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Эли Коэн"
        />
      </div>
      <div className="field">
        <label className="field-label">Email</label>
        <input
          className="input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
        />
      </div>
      <div className="field">
        <label className="field-label">Телефон</label>
        <div style={{ position: 'relative' }}>
          <div
            className="field-prefix"
            style={{
              borderColor: phoneTouched && !phoneValid ? 'var(--danger-fg)' : undefined,
            }}
          >
            <button
              type="button"
              onClick={() => setDialOpen((o) => !o)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '0 10px 0 12px',
                border: 'none',
                borderRight: '1px solid var(--hairline)',
                background: 'var(--bg-subtle)',
                fontSize: 13,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>{dial.flag}</span>
              <span className="mono">{dial.code}</span>
              <Icon name="chevronDown" size={11} />
            </button>
            <input
              className="input"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setPhoneTouched(true);
              }}
              placeholder={dial.cc === 'IL' ? '50 000 0000' : dial.cc === 'RU' ? '900 000 00 00' : 'номер телефона'}
              style={{ flex: 1, border: 'none', borderRadius: 0, background: 'transparent' }}
            />
          </div>
          {dialOpen && (
            <>
              <div
                onClick={() => {
                  setDialOpen(false);
                  setDialSearch('');
                }}
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  zIndex: 41,
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  boxShadow: 'var(--shadow-lg)',
                  padding: 4,
                }}
              >
                <div style={{ padding: '4px 4px 6px', borderBottom: '1px solid var(--hairline)' }}>
                  <input
                    className="input"
                    value={dialSearch}
                    onChange={(e) => setDialSearch(e.target.value)}
                    placeholder="Поиск страны или кода…"
                    autoFocus
                    style={{ height: 34, fontSize: 13 }}
                  />
                </div>
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {filteredDial.map((d) => (
                    <button
                      key={d.cc}
                      type="button"
                      onClick={() => {
                        setDial(d);
                        setDialTouched(true);
                        setDialOpen(false);
                        setDialSearch('');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        width: '100%',
                        padding: '6px 10px',
                        border: 'none',
                        background: d.cc === dial.cc ? 'var(--bg-subtle)' : 'transparent',
                        fontSize: 13,
                        textAlign: 'left',
                        borderRadius: 6,
                        cursor: 'pointer',
                        color: d.cc === dial.cc ? 'var(--fg)' : 'var(--fg-muted)',
                        fontWeight: d.cc === dial.cc ? 600 : 400,
                      }}
                    >
                      <span style={{ fontSize: 15 }}>{d.flag}</span>
                      <span className="mono" style={{ width: 56 }}>
                        {d.code}
                      </span>
                      <span style={{ flex: 1 }}>{d.label}</span>
                      {d.cc === dial.cc && <Icon name="check" size={12} />}
                    </button>
                  ))}
                  {filteredDial.length === 0 && (
                    <div style={{ padding: 12, textAlign: 'center', color: 'var(--fg-subtle)', fontSize: 13 }}>
                      Ничего не найдено
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        {phoneTouched && !phoneValid && (
          <div style={{ fontSize: 11.5, color: 'var(--danger-fg)', marginTop: 4 }}>
            Введите {PHONE_MIN}–{PHONE_MAX} цифр после {dial.code}
          </div>
        )}
      </div>

      {BRAND.messengerField && (
      <div className="field">
        <label className="field-label">
          Мессенджер для связи{' '}
          <span style={{ color: 'var(--fg-subtle)', fontWeight: 400 }}>— не обязательно</span>
        </label>
        <div
          className="field-prefix"
          style={{ padding: 0 }}
        >
          <div
            style={{
              display: 'inline-flex',
              padding: 3,
              background: 'var(--bg-subtle)',
              borderRight: '1px solid var(--hairline)',
            }}
          >
            {(['telegram', 'whatsapp'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setMessengerType(t)}
                style={{
                  padding: '0 10px',
                  height: 'calc(var(--row-h) - 6px)',
                  border: 'none',
                  background: messengerType === t ? 'var(--surface)' : 'transparent',
                  color: messengerType === t ? 'var(--fg)' : 'var(--fg-muted)',
                  borderRadius: 6,
                  fontSize: 12.5,
                  fontWeight: 500,
                  cursor: 'pointer',
                  boxShadow: messengerType === t ? 'var(--shadow-sm)' : 'none',
                }}
              >
                {t === 'telegram' ? 'Telegram' : 'WhatsApp'}
              </button>
            ))}
          </div>
          <input
            className="input"
            value={messengerValue}
            onChange={(e) => setMessengerValue(e.target.value)}
            placeholder={messengerType === 'telegram' ? '@username' : '+972 50 000 0000'}
            style={{ border: 'none', borderRadius: 0, background: 'transparent' }}
          />
        </div>
      </div>
      )}

      {installmentsAvailable && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: 12,
              background: 'var(--bg-subtle)',
              border: '1px solid var(--hairline)',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
              fontSize: 13,
              lineHeight: 1.4,
            }}
          >
            <input
              type="checkbox"
              checked={installments}
              onChange={(e) => setInstallments(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              Разбить на платежи
              <span style={{ color: 'var(--fg-subtle)', display: 'block', fontSize: 12 }}>
                Только для граждан Израиля. Доступно 1–{installmentsMax} платежей. Выбор — на
                следующей странице.
              </span>
            </span>
          </label>
          {installments && (
            <div className="field">
              <label className="field-label">Теудат зеут</label>
              <input
                className="input mono"
                inputMode="numeric"
                value={teudatZehut}
                onChange={(e) => {
                  setTeudatZehut(e.target.value.replace(/\D/g, '').slice(0, 9));
                  setTeudatTouched(true);
                }}
                placeholder="123456789"
                maxLength={9}
                style={{
                  borderColor: teudatTouched && !teudatValid ? 'var(--danger-fg)' : undefined,
                  letterSpacing: 1,
                }}
              />
              {teudatTouched && !teudatValid && (
                <div style={{ fontSize: 11.5, color: 'var(--danger-fg)', marginTop: 4 }}>
                  Введите 9 цифр номера удостоверения личности.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <button
        type="submit"
        className="btn btn-primary"
        disabled={!valid || submitting}
        style={{ justifyContent: 'center', height: 46, fontSize: 15, fontWeight: 600, marginTop: 4 }}
      >
        {submitting
          ? 'Перенаправляем…'
          : `Оплатить ${fmtAmountWithSym(discountedAmount ?? price, currency)}${
              product.subscriptionEnabled ? ' в месяц' : ''
            }`}
      </button>

      <div
        style={{
          fontSize: 11,
          color: 'var(--fg-dim)',
          textAlign: 'center',
          marginTop: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        <Icon name="lock" size={10} />
        Защищённый платёж. Данные карты не попадают к нам на сервер.
      </div>
    </form>

    {soldOut && (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          padding: 20,
        }}
      >
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-lg)',
            padding: '18px 20px',
            maxWidth: 360,
            textAlign: 'center',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>Продажи закрыты</div>
          <div style={{ fontSize: 13.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
            По этой ссылке больше нельзя оформить оплату.{' '}
            <a
              href={BRAND.supportUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--accent)', textDecoration: 'underline', textUnderlineOffset: 3 }}
            >
              Напишите нам
            </a>
            , чтобы получить новую ссылку.
          </div>
        </div>
      </div>
    )}
  </div>
  );
}
